import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { JSX, useState } from "react";
import { addNotification } from "@/store";
import { serviceLinks } from "@/lib/constants";

const SocialLinkedIn = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);
const SocialTwitter = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const SocialFacebook = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const SocialYoutube = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.54C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
  </svg>
);

const quickLinks = [
  { href: "/about", label: "About Us" },
  { href: "/blog", label: "Blog & Insights" },
  { href: "/media", label: "Media Centre" },
  { href: "/resources", label: "Resources" },
  { href: "/contact", label: "Contact Us" },
  { href: "/admin", label: "Client Portal" },
];

export function Footer(): JSX.Element {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setEmail("");
      addNotification(
        "success",
        "Subscribed! You'll receive our latest Updates.",
      );
    }, 1200);
  };

  return (
    <footer className="bg-[#1A1A1A] text-white">
      <div className="bg-[#6B1E2A] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
              />
            </pattern>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl font-semibold text-white">
              Ready to Take Control of Your Taxes?
            </h3>
            <p className="text-white/70 mt-1">
              Book a free 30-minute consultation with our experts today.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/contact"
              className="px-6 py-3 bg-[#C9A84C] text-[#1A1A1A] font-semibold rounded-lg hover:bg-[#E2C06B] transition-colors text-sm"
            >
              Book Free Consultation
            </Link>
            <a
              href="tel:+254703173405"
              className="px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors text-sm border border-white/20"
            >
              Call Us Now
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center justify-start group">
              <img
                src="/images/Rosewood Logo main version.png"
                alt="Rosewood Tax Consultancy"
                className="h-16 w-auto object-contain sm:h-20"
              />
              <div>
                <div className="font-serif text-lg font-semibold text-white">
                  Rosewood
                </div>
                <div className="text-[9px] font-medium tracking-[0.15em] text-[#C9A84C] uppercase">
                  Consultancy
                </div>
              </div>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              Kenya's trusted tax consultancy. Expert compliance, advisory, and
              representation services for businesses of all sizes.
            </p>
            <div className="flex items-center gap-3">
              {[
                { Icon: SocialLinkedIn, href: "#" },
                { Icon: SocialTwitter, href: "#" },
                { Icon: SocialFacebook, href: "#" },
                { Icon: SocialYoutube, href: "#" },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  className="w-9 h-9 bg-white/8 rounded-lg flex items-center justify-center hover:bg-[#6B1E2A] transition-colors text-white/70 hover:text-white"
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-[#C9A84C] mb-5">
              Our Services
            </h4>
            <ul className="space-y-2.5">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href as never}
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 bg-[#C9A84C]/50 rounded-full group-hover:bg-[#C9A84C] transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-[#C9A84C] mb-5">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href as never}
                    className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1.5 group"
                  >
                    <span className="w-1 h-1 bg-[#C9A84C]/50 rounded-full group-hover:bg-[#C9A84C] transition-colors" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h4 className="text-sm font-semibold tracking-wider uppercase text-[#C9A84C] mb-5">
                Contact
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="tel:+254703173405"
                    className="flex items-start gap-2.5 text-sm text-white/60 hover:text-white transition-colors"
                  >
                    <Phone
                      size={14}
                      className="mt-0.5 flex-shrink-0 text-[#C9A84C]"
                    />
                    +254 703 173405
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@rosewoodconsulting.co.ke"
                    className="flex items-start gap-2.5 text-sm text-white/60 hover:text-white transition-colors"
                  >
                    <Mail
                      size={14}
                      className="mt-0.5 flex-shrink-0 text-[#C9A84C]"
                    />
                    info@rosewoodconsulting.co.ke
                  </a>
                </li>
                <li>
                  <div className="flex items-start gap-2.5 text-sm text-white/60">
                    <MapPin
                      size={14}
                      className="mt-0.5 flex-shrink-0 text-[#C9A84C]"
                    />
                    Upper Hill, Nairobi, Kenya
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase text-[#C9A84C] mb-5">
              Updates
            </h4>
            <p className="text-sm text-white/60 mb-4 leading-relaxed">
              Get the latest Kenyan tax news, KRA updates, and compliance alerts
              delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletter} className="space-y-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full px-4 py-2.5 bg-white/8 border border-white/15 rounded-lg text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#C9A84C]/50 transition-colors"
              />
              <motion.button
                type="submit"
                disabled={submitting}
                whileTap={{ scale: 0.97 }}
                className="w-full px-4 py-2.5 bg-[#C9A84C] text-[#1A1A1A] font-semibold rounded-lg text-sm hover:bg-[#E2C06B] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  "Subscribing..."
                ) : (
                  <>
                    Subscribe <ArrowRight size={14} />
                  </>
                )}
              </motion.button>
            </form>
            <p className="text-xs text-white/30 mt-3">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} Rosewood Tax Consultancy. All rights
            reserved.
          </p>
          <div className="flex items-center gap-5">
            {["Privacy Policy", "Terms of Service", "Cookie Policy"].map(
              (item) => (
                <a
                  key={item}
                  href="#"
                  className="text-xs text-white/40 hover:text-white/70 transition-colors"
                >
                  {item}
                </a>
              ),
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
